import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Slider, Skeleton } from '@mui/material';
import { formatNumberWithSuffix, formatPrice } from '@src/services/package';
import { useTranslation } from 'react-i18next';
import Typography from '@src/new-components/Typography';
import apis from '@src/apis';
import { useSelector } from 'react-redux';
import CheckCycleIcon from '@src/assets/images/check-cycle.svg';
import LocalActivityIcon from '@src/assets/images/local-activity.svg';
import { delimitNumber } from '@src/utils/number';
import pluralize from '@src/utils/pluralize';
import CommonButton from '@src/new-components/Button';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import routes from '@src/constants/route';
import { Link } from 'react-router-dom';
import { COLOR } from '@src/styles/color';
import {
  StyledButtonArrowContainer,
  StyledRewardProgressTracker,
  StyledSliderContainer,
} from './index.style';
import TopLabels from './TopLabels';

const THUMB_NUMBER_MOBILE = 2;

function getMaxThumbNumber(windowWidth, bigMultiple) {
  if (windowWidth < 810) {
    return bigMultiple - 3;
  }
  if (windowWidth < 1130) {
    return bigMultiple - 2;
  }
  if (windowWidth < 1200) {
    return bigMultiple - 1;
  }
  return bigMultiple;
}

function formatMarkValue({ value, language, windowWidth }) {
  if (windowWidth < 810) {
    return formatNumberWithSuffix(value, language);
  }
  return delimitNumber(value, language);
}

const RewardProgressTracker = () => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { referral } = user;
  const { totalRevenue = 0 } = referral || {};

  const referralConfig = useSelector((state) => state.referralConfig);
  const { charsPerPoint } = referralConfig || {};
  const { language } = i18n;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxThumbNumber, setMaxThumbNumber] = useState(5);
  const [revenueRewardConfig, setRevenueRewardConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRevenueRewardConfig = async () => {
      setLoading(true);
      const response = await apis.referral.getRevenueRewardConfig();
      setRevenueRewardConfig(response);
      setMaxThumbNumber(
        getMaxThumbNumber(window.innerWidth, response?.bigMultiple),
      );
      setLoading(false);
    };
    fetchRevenueRewardConfig();
  }, []);

  const { baseRevenueAmount, pointRewardSmall, pointRewardBig, bigMultiple } =
    revenueRewardConfig || {};

  useEffect(() => {
    const handleResize = () => {
      setMaxThumbNumber(getMaxThumbNumber(window.innerWidth, bigMultiple));
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [bigMultiple]);

  const buildAllMarks = () => {
    const total = Math.ceil(
      (totalRevenue || bigMultiple * baseRevenueAmount) / baseRevenueAmount,
    );
    let length = total;
    if (total % bigMultiple !== 0) {
      length = total + 1;
    }

    length += bigMultiple;
    return Array.from({ length }, (_, idx) => {
      const value = baseRevenueAmount * (idx + 1);
      return { value };
    });
  };
  const groupMarks = (marks, groupSize) => {
    const grouped = [];
    for (let i = 0; i < marks.length; i += groupSize) {
      const group = marks.slice(i, i + groupSize).map((mark) => {
        const isLast = mark.value % (bigMultiple * baseRevenueAmount) === 0;
        const value = isLast
          ? charsPerPoint * pointRewardBig
          : charsPerPoint * pointRewardSmall;
        const isMobileSlider = groupSize === THUMB_NUMBER_MOBILE;
        const formattedPrice = formatPrice({
          vndPrice: mark.value,
          usdPrice: 0,
          isVietNam: true,
          language,
        });

        const topMarkLabels = isMobileSlider
          ? formattedPrice
          : `${t('accumulate')} ${formattedPrice}`;

        return {
          ...mark,
          labeld: true,
          label: (
            <Typography
              className={isLast ? 'mark-label' : ''}
              variant="body-16-regular "
              color={COLOR.descriptionGray}
            >
              <Typography variant="body-16-semibold">
                +
                {formatMarkValue({
                  value,
                  language,
                  windowWidth: window.innerWidth,
                })}
              </Typography>{' '}
              {pluralize(value, t('credit'), language)}
            </Typography>
          ),
          topMarkLabels,
        };
      });
      grouped.push(group);
    }
    return grouped;
  };

  const getGroupedMarks = () => groupMarks(buildAllMarks(), maxThumbNumber);

  const useCurrentMarks = (index, maxThumbNum) =>
    useMemo(() => {
      const groups = getGroupedMarks();
      const marks = groups[index];
      const marksNext = groups[index + 1];
      const markPrevious = groups[index - 1];
      if (!marks || marks.length === 0) return [];
      const lastVaue =
        marks?.[marks?.length - 1]?.value +
        (marksNext?.[0]?.value - marks?.[marks?.length - 1]?.value);

      return [
        {
          value: markPrevious?.[markPrevious?.length - 1]?.value
            ? markPrevious?.[markPrevious?.length - 1]?.value
            : 0,
          label: '',
          topMarkLabels: '',
        },
        ...marks,
        {
          value:
            lastVaue || marks?.[marks?.length - 1]?.value + baseRevenueAmount,
          label: '',
          topMarkLabels: '',
        },
      ];
    }, [index, maxThumbNum, language, totalRevenue]);

  const useMarksWithCustomIcons = (marksList, currentCredits) =>
    useMemo(
      () =>
        marksList.map((mark, idx) => {
          if (idx === 0 || idx === marksList.length - 1) {
            return {
              ...mark,
              icon: null,
              style: {
                backgroundColor: 'transparent !important',
                backgroundImage: 'none',
                zIndex: 1,
                opacity: 0,
              },
            };
          }
          const isAchieved = currentCredits >= mark.value;
          return {
            ...mark,
            icon: null,
            style: {
              width: 48,
              height: 48,
              color: 'white !important',
              zIndex: 999,
              borderRadius: '50%',
              backgroundColor: 'white',
              backgroundImage: `url(${
                isAchieved ? CheckCycleIcon : LocalActivityIcon
              })`,
              backgroundPosition: 'center',
              boxShadow: '0px 4px 16px 0px #00000014',
              backgroundRepeat: 'no-repeat',
              opacity: 1,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            },
          };
        }),
      [marksList, currentCredits],
    );

  const marksList = useCurrentMarks(currentIndex, maxThumbNumber);
  const marksWithCustomIcons = useMarksWithCustomIcons(marksList, totalRevenue);

  const indexShowMarks = useMemo(() => {
    const groupedMarks = getGroupedMarks();
    const groupedMarksWithoutLast = groupedMarks || [];

    return groupedMarksWithoutLast?.findIndex((array) =>
      array?.some((obj) => obj?.value > totalRevenue),
    );
  }, [getGroupedMarks(), totalRevenue]);

  useEffect(() => {
    if (indexShowMarks !== -1) {
      setCurrentIndex(indexShowMarks);
    }
  }, [indexShowMarks]);

  const hasPrevious = currentIndex > 0;
  const hasNext =
    currentIndex < getGroupedMarks().length &&
    totalRevenue >= marksList[marksList.length - 2]?.value;

  const handlePrevious = useCallback(() => {
    if (hasPrevious) setCurrentIndex((idx) => idx - 1);
  }, [hasPrevious]);

  const handleNext = useCallback(() => {
    if (hasNext) setCurrentIndex((idx) => idx + 1);
  }, [hasNext]);

  const minValue = marksList[0]?.value;
  const maxValue = marksList[marksList.length - 1]?.value;

  const getSliderSx = () => ({
    cursor: 'default',
    marginBottom: '14px',
    marginTop: '10px',
    '& .MuiSlider-track': {
      backgroundColor: 'gray',
      color: '#1E164E',
      height: '8px',
      border: 'none',
      zIndex: 99,
      borderRadius: '0% !important',
    },
    '& .MuiSlider-rail': {
      backgroundColor:
        totalRevenue >= marksList[marksList.length - 2]?.value
          ? '#1E164E'
          : '#ffffff',
      height: '8px',
      opacity: totalRevenue >= marksList[marksList.length - 2]?.value ? 1 : 0.5,
      borderRadius: '0% !important',
    },
    '& .MuiSlider-thumb': {
      display:
        totalRevenue &&
        totalRevenue < marksList[marksList.length - 2]?.value &&
        totalRevenue !== marksList[0]?.value
          ? 'block'
          : 'none',
      width: 14,
      height: 14,
      color: 'transparent',
      zIndex: 200,
      borderRadius: '50%',
      backgroundColor: '#fff',
      border: '1px solid #1E164E52',
      boxShadow: 'none!important',
      '&:hover': {
        boxShadow: '5px 10px transparent',
      },
      '&.Mui-active': {
        boxShadow: '5px 10px transparent',
      },
      '&:focus': {
        boxShadow: '5px 10px transparent',
      },
    },
    '& .MuiSlider-thumb:before': {
      boxShadow: '5px 10px transparent',
    },
    '& .MuiSlider-mark': {
      width: 48,
      height: 48,
      color: 'white !important',
      zIndex: 999,
      borderRadius: '50%',
      backgroundImage: 'none',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      boxShadow: '0px 4px 16px 0px #00000014',
      opacity: 1,
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
    ...marksWithCustomIcons.reduce((acc, mark, idx) => {
      acc[`& .MuiSlider-mark[data-index="${idx}"]`] = mark.style || {};
      return acc;
    }, {}),
    '& .MuiSlider-markLabel': {
      top: 52,
    },
    '& .MuiSlider-markLabelActive': {
      color: '#1C1C1E',
    },
  });

  const renderButtonArrow = (direction) => (
    <CommonButton
      onClick={direction === 'previous' ? handlePrevious : handleNext}
      aria-label="close"
      variant="outlineSmall"
      customStyle={{
        width: '32px',
        height: '32px',
        minWidth: '32px',
        borderRadius: '50%',
        backgroundColor: 'transparent',
      }}
      disabled={direction === 'previous' ? !hasPrevious : !hasNext}
    >
      {direction === 'previous' ? (
        <ArrowBackIosRoundedIcon
          sx={{ fontSize: '16px', color: COLOR.navyBlue }}
        />
      ) : (
        <ArrowForwardIosRoundedIcon
          sx={{ fontSize: '16px', color: COLOR.navyBlue }}
        />
      )}
    </CommonButton>
  );

  return (
    <StyledRewardProgressTracker>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          className="title-section-text"
          variant="body-20-bold"
          color={COLOR.descriptionGray}
        >
          {t('rewardsByAccumulation')}
        </Typography>
        <Link
          style={{
            color: COLOR.textPending,
            fontSize: '16px',
            fontWeight: 500,
          }}
          className="payment-history-referral-text"
          to={routes.REFERRED_ORDERS}
        >
          {t('paymentHistoryReferral')}
        </Link>
      </div>
      <Typography
        style={{ marginBottom: '16px' }}
        variant="body-14-regular"
        color={COLOR.descriptionUpload}
        className="description-accumulated-referral-reward-text"
      >
        {t('descriptionAccumulatedReferralReward')}
      </Typography>

      <Typography
        style={{ marginBottom: '8px' }}
        variant="body-14-regular"
        color={COLOR.descriptionUpload}
        className="accumulate-current-text"
        dangerouslySetInnerHTML={{
          __html: t('accumulateCurrent', {
            value: formatPrice({
              vndPrice: totalRevenue,
              usdPrice: totalRevenue,
              isVietNam: true,
              language,
            }),
          }),
        }}
      />

      {loading ? (
        <Skeleton
          style={{ borderRadius: '12px' }}
          variant="rectangular"
          width="100%"
          height={196}
        />
      ) : (
        <StyledSliderContainer padding="0px 0px 46px 0px" height="216px">
          {minValue !== undefined && maxValue !== undefined && (
            <TopLabels
              marksList={marksList}
              minValue={minValue}
              maxValue={maxValue}
            />
          )}
          <Slider
            value={totalRevenue}
            step={null}
            marks={marksWithCustomIcons}
            min={minValue}
            max={maxValue}
            valueLabelDisplay="auto"
            getAriaValueText={() => null}
            components={{
              ValueLabel: null,
            }}
            sx={getSliderSx()}
          />

          <StyledButtonArrowContainer>
            {renderButtonArrow('previous')}
            {renderButtonArrow('next')}
          </StyledButtonArrowContainer>
        </StyledSliderContainer>
      )}
    </StyledRewardProgressTracker>
  );
};

export default RewardProgressTracker;
